import React, { useEffect, useMemo, useState } from "react";
import { UsersIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import CustomTableComponent from "../CustomTableComponent";
import UserPDFModal from "../client/UserPDFModal";
import BusinessCertificationModal from "../client/BusinessCertification";

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

const MemberListModal = ({
  open,
  loading,
  onClose,
  partners,
  headers,
  onRowClick,
  year,
  years,
  onYearChange,
}) => (
  <AnimatePresence>
    {open && (
      <motion.div
        {...animation}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      >
        <motion.div
          {...animation}
          className="relative bg-white w-full max-w-7xl h-[82vh] mx-2 md:mx-0 rounded-xl p-6 border border-gray-200 flex flex-col"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mr-4">
            <h2 className="text-xl font-semibold text-gray-800">
              Danh sách hội viên mới {year}
            </h2>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Năm</span>
              <select
                value={year}
                onChange={(e) => onYearChange(Number(e.target.value))}
                className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 outline-none focus:border-indigo-300"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-gray-500">Đang tải dữ liệu...</p>
            ) : (
              <CustomTableComponent
                headers={headers}
                data={partners}
                onRowClick={onRowClick}
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

const MemberDetailModal = ({ open, loading, onClose, partner }) => {
  if (!open || !partner) return null;

  const representative = partner?.users?.find(
    (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
  );

  const InfoItem = ({ label, value }) => (
    <div>
      <p className="text-[13px] text-gray-500">{label}</p>
      <p className="text-[15px] text-gray-800 font-medium">{value || "----"}</p>
    </div>
  );

  function formatDate(dateString) {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    return d.toLocaleDateString("vi-VN");
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
        exit={{ opacity: 0, y: 10, transition: { duration: 0.2 } }}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.3 } }}
          exit={{ opacity: 0, y: 10, transition: { duration: 0.2 } }}
          className="relative bg-white w-full max-w-4xl h-[90vh] mx-2 rounded-xl p-6 border border-gray-200 flex flex-col"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <h2 className="text-xl font-bold text-gray-800 mb-4 flex-shrink-0">
            Chi tiết Hội viên
          </h2>

          <div className="flex-1 overflow-y-auto pr-2">
            {loading ? (
              <p className="text-gray-500">Đang tải thông tin...</p>
            ) : (
              <div className="space-y-7 text-[15px] text-gray-800">
                <section>
                  <div className="flex items-center gap-5 mb-3">
                    <img
                      src={
                        partner.userAvatar ||
                        `https://ui-avatars.com/api/?name=${partner.companyName}`
                      }
                      alt="Logo"
                      className="w-20 h-20 rounded-2xl object-contain border bg-gray-100 flex-shrink-0"
                    />

                    <div className="min-w-0">
                      <div className="font-bold text-2xl break-words whitespace-normal leading-snug">
                        {partner.companyName}
                      </div>

                      <div className="text-sm text-gray-500">
                        Tên tiếng Anh:{" "}
                        <span className="font-normal break-words">
                          {partner.companyNameEn || "----"}
                        </span>
                      </div>

                      <div className="text-sm text-gray-500">
                        Tên viết tắt:{" "}
                        <span className="font-normal break-words">
                          {partner.companyShortName || "----"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <InfoItem label="Mã số thuế" value={partner.taxCode} />
                    <InfoItem
                      label="Ngày thành lập"
                      value={formatDate(partner.establishedDate)}
                    />
                    <InfoItem
                      label="Địa chỉ đơn vị"
                      value={partner.businessAddress}
                    />
                    <InfoItem
                      label="Điện thoại đơn vị"
                      value={partner.userPhone}
                    />
                    <InfoItem label="Email đơn vị" value={partner.userEmail} />
                    <InfoItem
                      label="Địa chỉ website"
                      value={partner.websiteOrHotline}
                    />
                    <InfoItem
                      label="Ngành nghề kinh doanh"
                      value={partner.businessField}
                    />
                  </div>
                </section>

                <hr className="border-t border-gray-200 my-5" />
                <section>
                  <h3 className="font-semibold text-lg mb-2">
                    Tổng Giám đốc / Giám đốc
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <InfoItem label="Họ và tên" value={partner.ceoFullName} />
                    <InfoItem
                      label="Sinh nhật"
                      value={formatDate(partner.ceoDateOfBirth)}
                    />
                    <InfoItem label="Số điện thoại" value={partner.ceoPhone} />
                    <InfoItem label="Email" value={partner.ceoEmail} />
                  </div>
                </section>

                <hr className="border-t border-gray-200 my-5" />
                <section>
                  <h3 className="font-semibold text-lg mb-2">Người đại diện</h3>
                  {representative ? (
                    <div className="grid md:grid-cols-2 gap-4">
                      <InfoItem
                        label="Họ và tên"
                        value={representative.userFullName}
                      />
                      <InfoItem
                        label="Sinh nhật"
                        value={formatDate(representative.dateOfBirth)}
                      />
                      <InfoItem label="SĐT" value={representative.userPhone} />
                      <InfoItem
                        label="Email"
                        value={representative.userEmail}
                      />
                      <InfoItem
                        label="Chức vụ"
                        value={representative?.CompaniesHasUsers?.userPosition}
                      />
                    </div>
                  ) : (
                    <div className="text-gray-500">Chưa có người đại diện</div>
                  )}
                </section>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const NewMemberInYearCard = () => {
  const currentYear = new Date().getFullYear();

  const [partners, setPartners] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [certCompany, setCertCompany] = useState(null);

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfCompany, setPdfCompany] = useState(null);

  const [year, setYear] = useState(Math.max(2024, currentYear));

  const years = useMemo(() => {
    const start = 2024;
    const ys = [];
    for (let y = currentYear; y >= start; y -= 1) ys.push(y);
    return ys;
  }, [currentYear]);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const res = await ApiCustom.get("/company-shows");
      const companies = res?.data?.data?.data?.users || [];

      const filtered = companies.filter((c) => {
        if (c?.approvalStatus !== "Đã duyệt") return false;

        const joinAtValue = c?.joinAt;

        if (year === 2024) {
          if (joinAtValue == null) return true;
          const joinAt = new Date(joinAtValue);
          if (Number.isNaN(joinAt.getTime())) return false;
          return joinAt.getFullYear() === 2024;
        }

        if (joinAtValue == null) return false;
        const joinAt = new Date(joinAtValue);
        if (Number.isNaN(joinAt.getTime())) return false;
        return joinAt.getFullYear() === year;
      });

      setPartners(filtered);
    } catch (err) {
      console.error("Lỗi khi tải danh sách hội viên:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await ApiCustom.get(`/company-show-by-id/${id}`);
      setSelectedPartner(res?.data?.data);
      setDetailOpen(true);
    } catch (err) {
      console.error("Lỗi khi tải chi tiết hội viên:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [year]);

  const headers = [
    {
      key: "userAvatar",
      label: "Logo",
      render: (item) => (
        <div className="w-12 h-12 rounded-lg overflow-hidden mx-auto bg-gray-50">
          <img
            src={
              item?.userAvatar ||
              `https://ui-avatars.com/api/?name=${item?.companyName}&size=200&background=random&color=fff&bold=true`
            }
            alt={item?.companyName}
            className="w-full h-full object-contain"
          />
        </div>
      ),
    },
    {
      key: "companyName",
      label: "Tên đơn vị",
      render: (item) => (
        <div>
          <p className="font-medium text-gray-800">{item.companyName}</p>
          <p className="text-sm text-gray-500">
            {
              item?.users?.find(
                (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
              )?.userFullName
            }
          </p>
        </div>
      ),
    },
    { key: "taxCode", label: "Mã số thuế" },
    { key: "userPhone", label: "SĐT" },
    { key: "userEmail", label: "Email" },
    {
      key: "actions",
      label: "Thao tác",
      render: (item) => (
        <div className="flex justify-center space-x-1">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setPdfCompany(item);
              setPdfOpen(true);
            }}
            className="btn btn-outline btn-success btn-sm py-1 rounded"
            title="Xem thiệp chào mừng"
          >
            Chào mừng
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setCertCompany(item);
              setCertOpen(true);
            }}
            className="btn btn-outline btn-primary btn-sm py-1 rounded"
            title="Xem chứng nhận"
          >
            Chứng nhận
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full sm:w-auto shadow-md flex flex-col items-center text-center p-5 rounded-2xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50/20 transition-all cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <div className="p-3 bg-indigo-50 rounded-full mb-3">
          <UsersIcon className="h-6 w-6 text-indigo-600" />
        </div>
        <p className="text-lg text-black mb-1">Hội viên mới {year}</p>
        <p className="text-3xl font-bold">{partners.length}</p>
      </motion.div>

      <MemberListModal
        open={isOpen}
        loading={loading}
        onClose={() => setIsOpen(false)}
        partners={partners}
        headers={headers}
        onRowClick={(item) => fetchPartnerDetail(item.id)}
        year={year}
        years={years}
        onYearChange={setYear}
      />

      <MemberDetailModal
        open={detailOpen}
        loading={detailLoading}
        onClose={() => setDetailOpen(false)}
        partner={selectedPartner}
      />

      <UserPDFModal
        isOpen={pdfOpen}
        onClose={() => {
          setPdfOpen(false);
          setPdfCompany(null);
        }}
        company={pdfCompany}
      />

      <BusinessCertificationModal
        isOpen={certOpen}
        onClose={() => {
          setCertOpen(false);
          setCertCompany(null);
        }}
        company={certCompany}
      />
    </>
  );
};

export default NewMemberInYearCard;